import type { ReactNode } from 'react';
import { T } from 'gt-react';
import type { CashReceiptKeyingConfig } from '@erp/shared-types';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

export type CashReceiptKeyingConfigFieldsProps = {
  value: CashReceiptKeyingConfig;
  onChange: (next: CashReceiptKeyingConfig) => void;
  disabled?: boolean;
};

function SettingSelect<K extends keyof CashReceiptKeyingConfig>({
  id,
  label,
  help,
  value,
  options,
  disabled,
  onChange,
}: {
  id: string;
  label: ReactNode;
  help: ReactNode;
  value: CashReceiptKeyingConfig[K];
  options: { value: CashReceiptKeyingConfig[K]; label: ReactNode }[];
  disabled?: boolean;
  onChange: (next: CashReceiptKeyingConfig[K]) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => {
          const option = options.find((candidate) => candidate.value === next);
          if (option) onChange(option.value);
        }}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{help}</p>
    </div>
  );
}

/**
 * How `load_cash_receipt_work_item` turns a receipt and its remittance into
 * what gets keyed — the AR department's rules, stored on the agent's
 * `config.cashReceiptKeying` so they change without a deploy.
 */
export function CashReceiptKeyingConfigFields({
  value,
  onChange,
  disabled,
}: CashReceiptKeyingConfigFieldsProps) {
  return (
    <div className="space-y-4">
      <SettingSelect<'invoiceApplications'>
        id="cashReceiptKeying-invoiceApplications"
        disabled={disabled}
        label={
          <T context="label: how remittance lines become invoice rows when keying a cash receipt">
            Several lines for one invoice
          </T>
        }
        help={
          <T context="help text for the remittance line grouping setting">
            Some ledgers take only one row per invoice on the receipt screen.
          </T>
        }
        value={value.invoiceApplications}
        options={[
          {
            value: 'per_invoice',
            label: (
              <T context="option: add remittance lines for the same invoice into one row">
                Add them into one row
              </T>
            ),
          },
          {
            value: 'per_remittance_line',
            label: (
              <T context="option: key each remittance line as its own row">
                Key each line as its own row
              </T>
            ),
          },
        ]}
        onChange={(invoiceApplications) =>
          onChange({ ...value, invoiceApplications })
        }
      />
      <SettingSelect<'unresolvedRemittanceLines'>
        id="cashReceiptKeying-unresolvedRemittanceLines"
        disabled={disabled}
        label={
          <T context="label: what to do with remittance lines that matched no invoice">
            Lines that match no invoice
          </T>
        }
        help={
          <T context="help text for the unmatched remittance line setting">
            The procedure&apos;s steps say what happens to them, such as a
            suspense line. A receipt with no matched lines at all is always
            handed to a person.
          </T>
        }
        value={value.unresolvedRemittanceLines}
        options={[
          {
            value: 'allow',
            label: (
              <T context="option: key the matched lines and leave unmatched remittance lines to the procedure">
                Key the rest; the procedure handles them
              </T>
            ),
          },
          {
            value: 'block',
            label: (
              <T context="option: hand the receipt to a person when a remittance line is unmatched">
                Hand the receipt to a person
              </T>
            ),
          },
        ]}
        onChange={(unresolvedRemittanceLines) =>
          onChange({ ...value, unresolvedRemittanceLines })
        }
      />
      <SettingSelect<'otherCustomersInvoices'>
        id="cashReceiptKeying-otherCustomersInvoices"
        disabled={disabled}
        label={
          <T context="label: whether a receipt may pay invoices of other customers">
            Invoices belonging to another customer
          </T>
        }
        help={
          <T context="help text for the other customers' invoices setting">
            For example, a parent company paying for its subsidiaries.
          </T>
        }
        value={value.otherCustomersInvoices}
        options={[
          {
            value: 'allow',
            label: (
              <T context="option: key other customers' invoices under the receipt's customer">
                Key them under the receipt&apos;s customer
              </T>
            ),
          },
          {
            value: 'block',
            label: (
              <T context="option: hand the receipt to a person when it pays another customer's invoice">
                Hand the receipt to a person
              </T>
            ),
          },
        ]}
        onChange={(otherCustomersInvoices) =>
          onChange({ ...value, otherCustomersInvoices })
        }
      />
      <SettingSelect<'customerlessReceipts'>
        id="cashReceiptKeying-customerlessReceipts"
        disabled={disabled}
        label={
          <T context="label: what to do with a cash receipt that has no customer">
            Receipts with no customer
          </T>
        }
        help={
          <T context="help text for the customerless receipt setting">
            When the invoices belong to several customers, the one with the most
            invoices is used.
          </T>
        }
        value={value.customerlessReceipts}
        options={[
          {
            value: 'from_invoices',
            label: (
              <T context="option: use the customer the receipt's invoices belong to">
                Use the invoices&apos; customer
              </T>
            ),
          },
          {
            value: 'block',
            label: (
              <T context="option: hand a receipt with no customer to a person">
                Hand the receipt to a person
              </T>
            ),
          },
        ]}
        onChange={(customerlessReceipts) =>
          onChange({ ...value, customerlessReceipts })
        }
      />
    </div>
  );
}
